<script setup lang="ts">
  import { Button, ButtonAppearance, ButtonSize, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { Rating } from '@travelata-libs/ui-shared';
  import { computed, onMounted } from 'vue';
  import type { Product } from '@/entities/product';
  import type { SearchCriteria } from '@/entities/searchCriteria';
  import BannerDecor from './BannerDecor.vue';
  import { trackDreamTourClick, trackDreamTourImpression } from '../lib/dreamTourAnalytics';
  import { getDreamTourBannerView } from '../lib/dreamTourView';
  import { useDreamTourLottery } from '../model/useDreamTourLottery';

  interface DreamTourBannerProps {
    product: Product;
    searchCriteria: SearchCriteria;
    cardPosition: number;
  }

  defineProps<DreamTourBannerProps>();

  const { lottery } = useDreamTourLottery();

  const view = computed(() => (lottery.value ? getDreamTourBannerView(lottery.value) : null));

  const open = (): void => {
    if (!lottery.value) return;

    trackDreamTourClick(lottery.value.id);
    window.open(lottery.value.checkoutLink, '_blank', 'noopener');
  };

  onMounted(() => {
    if (lottery.value) trackDreamTourImpression(lottery.value.id);
  });
</script>

<template>
  <section
    v-if="view"
    class="dream-tour-banner"
    data-testid="dream-tour-banner"
  >
    <div class="dream-tour-banner__hotel">
      <img
        class="dream-tour-banner__image"
        :src="view.imageUrl"
        :alt="view.hotelName"
        loading="lazy"
        data-testid="dream-tour-banner-image"
      />
      <span
        class="dream-tour-banner__shade"
        aria-hidden="true"
      />
      <Rating
        class="dream-tour-banner__rating"
        :rating="view.rating"
        :reviews-count="view.reviewsCount"
      />
      <div class="dream-tour-banner__hotel-info">
        <Typo
          :variant="TypoVariant.BodyMMedium"
          tag="p"
        >
          {{ view.hotelName }}
        </Typo>
        <Typo
          :variant="TypoVariant.BodyXSRegular"
          tag="p"
        >
          {{ view.hotelLocation }}
        </Typo>
      </div>
    </div>

    <div class="dream-tour-banner__card">
      <BannerDecor variant="dream-tour" />

      <div class="dream-tour-banner__content">
        <div class="dream-tour-banner__text">
          <Typo
            class="dream-tour-banner__title"
            :variant="TypoVariant.Heading5"
            tag="p"
          >
            {{ view.title }}
          </Typo>
          <Typo
            :variant="TypoVariant.BodyMRegular"
            tag="p"
          >
            {{ view.subtitle }}
          </Typo>
        </div>

        <Button
          class="dream-tour-banner__action"
          :appearance="ButtonAppearance.Tertiary"
          :size="ButtonSize.M"
          data-testid="dream-tour-banner-action"
          @click="open"
        >
          {{ view.action }}
        </Button>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
  $hotel-top: 12px;
  $hotel-height: 227px;
  $card-top: 116px;
  $hotel-overlap: $hotel-top + $hotel-height - $card-top;

  .dream-tour-banner {
    display: flex;
    flex-direction: column;
    isolation: isolate;

    &__hotel {
      border: 2px solid var(--color-bg-primary);
      border-radius: var(--rounding-xl);
      box-shadow: var(--shadow-s);
      box-sizing: border-box;
      height: $hotel-height;
      margin: $hotel-top var(--spacing-m) 0;
      overflow: hidden;
      position: relative;
      z-index: 1;
    }

    &__image {
      display: block;
      height: 100%;
      object-fit: cover;
      width: 100%;
    }

    &__shade {
      background: linear-gradient(0deg, var(--color-bg-inverse) 0%, rgb(61 78 108 / 0.5) 31%, rgb(89 114 159 / 0) 61%);
      inset: 0;
      pointer-events: none;
      position: absolute;
    }

    &__rating {
      left: var(--spacing-s);
      position: absolute;
      top: var(--spacing-s);
    }

    &__hotel-info {
      bottom: var(--spacing-s);
      color: var(--color-text-inverse);
      display: flex;
      flex-direction: column;
      left: var(--spacing-s);
      position: absolute;
      right: var(--spacing-s);
    }

    &__card {
      background: linear-gradient(
        217deg,
        var(--color-yellow-40) 0%,
        rgb(249 127 3) 18%,
        rgb(250 127 3) 52%,
        rgb(231 115 2) 98%
      );
      border-radius: var(--rounding-l);
      box-sizing: border-box;
      margin-top: -$hotel-overlap;
      overflow: hidden;
      padding: calc(#{$hotel-overlap} + var(--spacing-m)) var(--spacing-m) var(--spacing-m);
      position: relative;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      position: relative;
    }

    &__text {
      color: var(--color-text-inverse);
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
    }

    &__title {
      max-width: 255px;
    }

    &__action {
      border: none;
      width: 100%;
    }
  }
</style>
